import { useEffect, useRef } from "react";

/**
 * Tracks how long the caller is visible (not just mounted) under `scopeKey`
 * and reports the seconds since the previous report, so the receiver adds
 * the pieces up. Time with the browser tab hidden doesn't count.
 *
 * A report is sent whenever the page is hidden or closed (tab switch,
 * minimise, screen lock, closing the tab or browser), when `scopeKey`
 * changes and on unmount. Closing a page never unmounts React, so reporting
 * only on unmount would lose the longest sessions.
 *
 * `scopeKey` (e.g. a channel id) is passed back to `onReport` so time is
 * always credited to the scope that was active while it accumulated.
 * An undefined key pauses tracking.
 */
const useVisibleDuration = <Key extends string>(
    scopeKey: Key | undefined,
    onReport: (scopeKey: Key, durationSeconds: number) => void
) => {
    const onReportRef = useRef(onReport);
    onReportRef.current = onReport;

    useEffect(() => {
        if (!scopeKey) return undefined;

        let visibleSince: number | null = document.hidden ? null : Date.now();

        // Report the visible time since the last report, then stop counting.
        const flush = () => {
            if (visibleSince === null) return;
            const seconds = Math.round((Date.now() - visibleSince) / 1000);
            visibleSince = null;
            if (seconds > 0) onReportRef.current(scopeKey, seconds);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                flush();
            } else if (visibleSince === null) {
                visibleSince = Date.now();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pagehide", flush);

        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
            window.removeEventListener("pagehide", flush);
            flush();
        };
    }, [scopeKey]);
};

export default useVisibleDuration;
